import moment from 'moment';
import '../../bins/parts/bin-path-name';
import '../../common/bn-modal-button';
import '../../common/bn-loading';
import '../../common/bn-property';
import '../../common/bn-action';
import '../../common/bn-icon';
import './work-order-review-device';
import './work-order-review-bin';
import '../../devices/parts/device-monitoring-chart';
import './reject-probation-modal';
import { LitEntity, html, css } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class ProbationReviewListItem extends LitEntity {
  static get properties() {
    return {
      binHref: { type: String, attribute: false },
      binAddress: { type: String, attribute: false },
      deviceHref: { type: String, attribute: false },
      hide: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
        max-height: 1500px;
        transition: opacity .2s, max-height .4s, margin .4s;
      }
      :host([hide]) {
        opacity: 0;
        max-height: 0px;
        margin: 0;
        pointer-events: none;
      }
      .header {
        display: flex;
        width: 100%;
        justify-content: space-between;
        margin-bottom: 1em;
        flex-direction: row;
      }
      .header bn-property {
        margin: 0;
      }
      .location {
        display: flex;
      }
      bn-icon {
        font-size: 3.125em;
        margin-right: 1.25rem;
        margin-left: 0.5rem;
        color: var(--blue-60);
      }
      .bin-name {
        display: inline-block;
        flex-direction: row;
        margin-bottom: .5em;
        --bin-link-color: var(--blue-50);
      }
      .bin-name bn-property {
        font-weight: bold;
      }
      .body {
        display: grid;
        flex-wrap: wrap;
        grid-template-columns: 1fr 2fr;
        grid-gap: 1em;
      }
      .body .well {
        padding: 1em;
        display: flex;
        flex-direction: column;
      }
      .body .well h4 {
        margin-top: 0;
      }
      .body .well .info {
        font-size: .9em;
        flex-grow: 1;
      }
      .body .bin {
        flex-grow: 2;
      }
      .body .readings {
        grid-column: 1 / 3;
      }
      bn-property {
        margin: .5em 2em 0 0;
        white-space: unset;
      }
      device-monitoring-chart {
        margin: 0;
      }
      .actions {
        display: flex;
        align-items: flex-start;
        justify-content: flex-end;
      }
      .actions > * {
        white-space: nowrap;
        margin: 0 0 0 .5em;
      }
      @media(max-width: 1200px) {
        .body .well {
          grid-column: 1 / 3;
        }
        bn-property + bn-property {
          margin: .5em 0 0 0;
        }
        .actions {
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-start;
        }
        .actions > * {
          margin: 0 0 .5em 0;
        }
      }
    `];
  }

  render() {
    if (this.loading) {
      return html`
        <bn-surface>
          <bn-loading></bn-loading>
        </bn-surface>
      `;
    }

    return html`
      <bn-surface class="work-order">
        <div class="header">
          ${this._renderLocation()}
          ${this._renderActions()}
        </div>

        <div class="body">
          ${this._renderReadings()}
        </div>
      </bn-surface>
    `;
  }

  _renderLocation() {
    const { createdAt, endDate } = (this.entity && this.entity.properties) || {};

    return html`
      <div class="location">
        <bn-icon name="fas fa-calendar-day"></bn-icon>
        <div class="info">
          <div class="bin-name">
            <bn-property stacked label="Probation of">
              <bin-path-name .href="${this.binHref}" bin-link-path="/bins/show/overview" @update="${this._onBinUpdate}"></bin-path-name>
            </bn-property>
            ${this.binAddress ? this.binAddress : ''}
          </div>

          <bn-property stacked label="Probation Period">
            ${this._formatDate(createdAt)} - ${this._formatDate(endDate)}
          </bn-property>
        </div>
      </div>
    `;
  }

  _renderActions() {
    const actions = [];
    if (this.entity.hasAction('accept')) {
      actions.push(html`
        <bn-action
          .href="${this.href}"
          suppressheader
          actionname="accept"
          submittext="Accept"
          @response="${this._onResponse}"
        ></bn-action>
      `);
    }

    if (this.entity.hasAction('reject')) {
      actions.push(html`
        <bn-modal-button button-class="button--containedError">
          <reject-probation-modal
            .href="${this.href}"
            slot="modal"
            @response="${this._onResponse}"
          ></reject-probation-modal>
          <span slot="text">Reject</span>
        </bn-modal-button>
      `);
    }

    return html`<div class="actions">${actions}</div>`;
  }

  _renderReadings() {
    return html`
      <div class="readings well">
        <h4>Readings</h4>
        <device-monitoring-chart
          visible-layers="${JSON.stringify(['levels', 'battery', 'cell', 'batteryTemperature'])}"
          .href="${this.deviceHref}"
        ></device-monitoring-chart>
      </div>
    `;
  }

  _formatDate(date) {
    return moment(date).format('MMM D');
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.binHref = this.getHref('https://api.binsentry.com/rel/bin');
    this.completedByUserHref = this.getHref('https://api.binsentry.com/rel/work-order-completed-by');
  }

  _onBinUpdate(e) {
    const { entity } = e.detail;
    this.deviceHref = (entity && entity.getSubEntityByClass('device') || {}).href;
    this.binAddress = entity && entity.properties.location.address;
  }

  _onResponse() {
    this.hide = true;
  }
}

customElements.define('probation-review-list-item', ProbationReviewListItem);
